/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from "react";
import { Button, Modal, Spinner } from "react-bootstrap";
import { ElectricityAgreementRenewalPlan, ElectricityAgreementRenewalPlanFilters } from "../models";
import { ModalProps } from "common/common-models";
import UpdateElectricityAgreementRenewalPlanData from "../components/UpdateElectricityAgreementRenewalPlanData";
import { ElectricityPackagesService } from "components/ElectricityPackages/ElectricityPackages.service";
import { ContractPeriod, ElectricityPackageType, IElectricityPackage, IElectricityPackageWithMarginal } from "components/ElectricityPackages/models";
import { isNotNullOrUndefined } from "common/data-util";
import { ElectricityAgreementRenewalPlansService, UpdateAgreementRenewalPlanRequest } from "../ElectricityAgreementRenewalPans.service";
import { ElectricityAgreementsService } from "components/ElectricityAgreements/ElectricityAgreements.service";
import { getMarginal } from "../components/UpdateElectricityAgreementRenewalPlanPackageForm";
import { isJointInvoicePossible } from "helpers/validator";

interface UpdateElectricityAgreementRenewalPlanModalProps extends ModalProps {
    renewalPlan?: ElectricityAgreementRenewalPlan;
    filters: ElectricityAgreementRenewalPlanFilters;
    selectedRenewalPlanIds: number[];
    excludedRenewalPlanIds: number[];
    allRenewalPlansSelected: boolean;
    packages: IElectricityPackage[];
}

export interface UpdateElectricityAgreementRenewalPlanModalForm {
        validated: boolean;
        consumptionPackageId?: number;
        nameEt?: string;
        basePriceCentsKwh?: number;
        dayTariffCentsKwh?: number;
        nightTariffCentsKwh?: number;
        consumptionMarginal?: number;
        monthlyFeeEuros?: number;
        fixedPackage?: boolean;
        marketPricePackage?: boolean;
        dynamicPackage?: boolean;
        twoTariffPackage?: boolean;
        hasProduction?: boolean;
        productionMarginal?: number;
        contractPeriodId?: number;
}

export type ElectricityPackageUnion = IElectricityPackage | IElectricityPackageWithMarginal;

type PackageFlags = {
    marketPricePackage: boolean;
    twoTariffPackage: boolean;
    dynamicPackage: boolean;
};

export const isFixedPackage = (pckg: PackageFlags) => {
    return !pckg.marketPricePackage && !pckg.twoTariffPackage && !pckg.dynamicPackage;
}

export function UpdateElectricityAgreementRenewalPlanModal(props: UpdateElectricityAgreementRenewalPlanModalProps) {
    const { renewalPlan } = props;
    const [form, setForm] = useState<UpdateElectricityAgreementRenewalPlanModalForm>({
        validated: false
    });

    const [saving, setSaving] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [packages, setPackages] = useState<ElectricityPackageUnion[]>([]);
    const [contractPeriods, setContractPeriods] = useState<ContractPeriod[]>([]);

    const findPackage = <T extends ElectricityPackageUnion> (
        packages: T[],
        publicPackageId?: number
    ) => {
        if (!publicPackageId || !packages.length) return undefined;
        
        return packages.find(p => p.publicId === publicPackageId);
    }

   useEffect(() => {
    const fetchData = async () => {
        try {
            const contractPeriods = await ElectricityAgreementsService.getElectricityContractPeriods();
            setContractPeriods(contractPeriods);

            if (renewalPlan) {
                const response = await ElectricityPackagesService.getCustomerElectricityPackages(
                    renewalPlan.customer.id,
                    renewalPlan.meteringPoints.map(mp => mp.eic)
                );

                const jointInvoicePossible = isJointInvoicePossible(!renewalPlan.customer.isPerson, renewalPlan.meteringPoints);

                const electricityPackages = response.filter(
                    pckg => pckg.jointInvoicePackage === jointInvoicePossible
                );

                setPackages(electricityPackages.filter(p => !p.dynamicPackage));
                const consumptionPackage = renewalPlan.consumptionPackage;

                setForm(prev => ({
                    ...prev,
                    ...(consumptionPackage && {
                        consumptionPackageId: consumptionPackage.id,
                        nameEt: consumptionPackage.nameEt,
                        basePriceCentsKwh: renewalPlan.basePriceCentsKwh,
                        dayTariffCentsKwh: renewalPlan.dayTariffCentsKwh,
                        nightTariffCentsKwh: renewalPlan.nightTariffCentsKwh,
                        consumptionMarginal: renewalPlan.consumptionMarginalCentsKwh,
                        monthlyFeeEuros: renewalPlan.monthlyFeeEuros,
                        fixedPackage: isFixedPackage(consumptionPackage),
                        twoTariffPackage: consumptionPackage.twoTariffPackage,
                        marketPricePackage: consumptionPackage.marketPricePackage,
                        dynamicPackage: consumptionPackage.dynamicPackage,
                        contractPeriodId: renewalPlan.contractPeriodId
                    }),
                    ...(renewalPlan.productionPackageId && {
                        hasProduction: true,
                        productionMarginal: renewalPlan.productionMarginalCentsKwh
                    })
                }));
            } else {
                setPackages(props.packages.filter(p => !p.dynamicPackage));

                const consumptionPackages = props.packages.filter(
                    p => p.type === ElectricityPackageType.CONSUMPTION
                );
                const productionPackages = props.packages.filter(
                    p => p.type === ElectricityPackageType.PRODUCTION
                );

                const matchingConsumptionPackage = findPackage(
                    consumptionPackages,
                    props.filters.publicPackageId
                );

                if(!matchingConsumptionPackage) {
                    setForm({
                        ...form,
                        hasProduction: true
                    })
                } else {
                    const matchingProductionPackage = productionPackages.find(p => p.meterType === matchingConsumptionPackage?.meterType);
                    const consumptionMarginal = getMarginal(matchingConsumptionPackage);
                    const productionMarginal = getMarginal(matchingProductionPackage);

                    setForm(prev => ({
                        ...prev,
                        hasProduction: true,
                        consumptionPackageId: matchingConsumptionPackage.id,
                        consumptionMarginal: consumptionMarginal,
                        productionMarginal: productionMarginal,
                        nameEt: matchingConsumptionPackage.nameEt,
                        basePriceCentsKwh: matchingConsumptionPackage.basePriceCentsKwh,
                        dayTariffCentsKwh: matchingConsumptionPackage.dayTariffCentsKwh,
                        nightTariffCentsKwh: matchingConsumptionPackage.nightTariffCentsKwh,
                        monthlyFeeEuros: matchingConsumptionPackage.monthlyFeeEuros,
                        fixedPackage: isFixedPackage(matchingConsumptionPackage),
                        twoTariffPackage: matchingConsumptionPackage.twoTariffPackage,
                        marketPricePackage: matchingConsumptionPackage.marketPricePackage,
                        dynamicPackage: matchingConsumptionPackage.dynamicPackage,
                        contractPeriodId: matchingConsumptionPackage.contractPeriod?.id
                    }));
                }
            }
        } finally {
            setIsLoading(false);
        }
    };

    fetchData();
    }, [props.renewalPlan]);

    const validateSubmit = () => {
        if (validate()) {
            submit();
        }
    }

    const validate = () => {
        setForm({ ...form, validated: true });

        const rules: boolean[] = [
            !!form.consumptionPackageId,
            form.twoTariffPackage ? isNotNullOrUndefined(form.dayTariffCentsKwh) && isNotNullOrUndefined(form.nightTariffCentsKwh) : true,
            form.fixedPackage ? isNotNullOrUndefined(form.basePriceCentsKwh) : true,
            isNotNullOrUndefined(form.consumptionMarginal),
            form.hasProduction ? isNotNullOrUndefined(form.productionMarginal) : true,
            !!form.contractPeriodId,
            isNotNullOrUndefined(form.monthlyFeeEuros)
        ];

        return rules.every(Boolean);
    }

    const submit = async () => {
        setSaving(true);

        const data : UpdateAgreementRenewalPlanRequest = {
            electricityPackageId: form.consumptionPackageId!,
            basePriceCentsKwh: form.fixedPackage ? form.basePriceCentsKwh : undefined,
            dayTariffCentsKwh: form.twoTariffPackage ? form.dayTariffCentsKwh : undefined,
            nightTariffCentsKwh: form.twoTariffPackage ? form.nightTariffCentsKwh : undefined,
            monthlyFeeEuros: form.monthlyFeeEuros,
            consumptionMarginalCentsKwh: form.consumptionMarginal!,
            productionMarginalCentsKwh: form.hasProduction ? form.productionMarginal : undefined,
            contractPeriodId: form.contractPeriodId!,
        };

        try {
            if(renewalPlan) {
                await ElectricityAgreementRenewalPlansService.updateAgreementRenewalPlan(
                    renewalPlan.id,
                    data
                );
            } else {
                if(props.allRenewalPlansSelected) {
                    await ElectricityAgreementRenewalPlansService.updateAgreementRenewalPlanByFilter({
                        filter: props.filters,
                        excludedAgreementRenewalPlanIds: props.excludedRenewalPlanIds,
                        data
                    });
                } else {
                    await ElectricityAgreementRenewalPlansService.updateAgreementRenewalPlanByIds({
                        agreementRenewalPlanIds: props.selectedRenewalPlanIds,
                        data
                    });
                }
            }
            
            props.onSuccess?.();
            props.handleModalHide();
        } catch(e) {
            console.error(e);
        } finally {
            setSaving(false);
        }
    }

    return (
      <Modal
        show={true}
        size="xl"
        dialogClassName="bg-light"
        onHide={props.handleModalHide}
        backdrop="static"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            { renewalPlan ? 'Muuda plaani' : 'Muuda plaane' }
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
            <UpdateElectricityAgreementRenewalPlanData 
                form={form}
                setForm={setForm}
                packages={packages}
                contractPeriods={contractPeriods}
                isLoading={isLoading}
            />
        </Modal.Body>
        <Modal.Footer className="d-flex flex-row align-items-end justify-content-end flex-wrap mb-1">
            <Button variant="info"
                    onClick={() => props.handleModalHide()}
                    type="button"
                    className={"d-flex align-items-center"}>
                <span className='px-2'>Tühista</span>
            </Button>
            <Button variant="primary" onClick={validateSubmit} type="button" disabled={saving}
                    className="d-flex align-items-center">
                {
                    !saving &&
                    <span className='px-2'>Muuda</span>
                }
                {
                    saving &&
                    <>
                        <span className='px-2'>Salvestamine…</span>
                        <Spinner size={"sm"} animation={"border"}/>
                    </>
                }
            </Button>
        </Modal.Footer>
      </Modal>    
    );
}